import axios from 'axios';
import { tokenManager } from './tokenManager';

const api = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL,
  withCredentials: true // Ensure cookies are sent with requests
});

// Request Interceptor: Inject the in-memory token
api.interceptors.request.use(
  (config) => {
    const token = tokenManager.getToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`; // Simplified to standard Bearer
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor: Handle 401 and Refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Reject if no response or if it's not a 401
    if (!error.response || error.response.status !== 401) {
      return Promise.reject(error);
    }


    const authEndpoints = ['/api/login', '/api/register', '/api/forgot-password', '/api/reset-password'];
    if (authEndpoints.some(endpoint => originalRequest.url.includes(endpoint))) {
      return Promise.reject(error);
    }

    // prevent infinite loop if the refresh endpoint itself returns 401
    if (originalRequest.url.includes('/api/refresh-token')) {
      tokenManager.clearToken();
      localStorage.removeItem('user');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // If already refreshing, queue this request
    if (tokenManager.isRefreshing()) {
      return new Promise((resolve) => {
        tokenManager.subscribeTokenRefresh((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          resolve(api(originalRequest));
        });
      });
    }

    // Start refreshing
    if (!originalRequest._retry) {
      originalRequest._retry = true;
      tokenManager.setIsRefreshing(true);

      try {
        // Call the refresh endpoint. 
        // Note: The browser automatically includes the HttpOnly cookie.
        const { data } = await api.post('/api/refresh-token');
        
        // Assuming response structure is { data: { token: '...' } } or similar
        // Adjust based on your actual backend response
        const newToken = data.data?.token || data.token; // Fallback check

        if (newToken) {
          tokenManager.setToken(newToken);
          tokenManager.onRefreshed(newToken);
          
          // Update the original request header
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        } else {
             throw new Error("No token returned from refresh");
        }

      } catch (refreshError) {
        console.error("Refresh failed:", refreshError);
        tokenManager.clearToken();
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        tokenManager.setIsRefreshing(false);
      }
    }

    return Promise.reject(error);
  }
);

export default api;